import * as React from "react";
import {useState, useRef, useEffect} from "react";
import {createPortal} from "react-dom";
import "./TagEditModal.css";

interface TagTextAreaProps {
    value: string;
    name: string;
    suggestions: string[];
    onChange: (name: string, value: string) => void;
}

export function TagTextArea({value, name, suggestions, onChange}: TagTextAreaProps) {
    const [filtered, setFiltered] = useState<string[]>([]);
    const [selected, setSelected] = useState(0);
    const ref = useRef<HTMLTextAreaElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
        if (listRef.current) {
            const active = listRef.current.querySelector("li.active") as HTMLElement | null;
            active?.scrollIntoView({block: "nearest"});
        }
    }, [selected]);

    function getCurrentTag(text: string, cursorPos: number) {
        const before = text.slice(0, cursorPos);
        const after = text.slice(cursorPos);
        const start = Math.max(before.lastIndexOf(","), before.lastIndexOf("\n")) + 1;
        const endComma = after.indexOf(",");
        const endNewline = after.indexOf("\n");''
        let endRel = after.length;
        if (endComma !== -1) endRel = Math.min(endRel, endComma);
        if (endNewline !== -1) endRel = Math.min(endRel, endNewline);
        return {tag: text.slice(start, cursorPos + endRel).trim(), start, end: cursorPos + endRel};
    }

    function getDropdownPos() {
        if (!ref.current) return null;
        const rect = ref.current.getBoundingClientRect();
        return {top: rect.top, left: rect.left, width: rect.width};
    }

    function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        const val = e.target.value;
        onChange(name, val);
        const {tag} = getCurrentTag(val, e.target.selectionStart ?? 0);
        if (tag.length > 0) {
            const f = suggestions
                .filter(s => s.toLowerCase().includes(tag.toLowerCase()) && s.toLowerCase() !== tag.toLowerCase())
                .slice(0, 8);
            setFiltered(f);
            setSelected(0);
        } else {
            setFiltered([]);
        }
    }

    function applySuggestion(suggestion: string) {
        const textarea = ref.current;
        if (!textarea) return;
        const {start, end} = getCurrentTag(value, textarea.selectionStart ?? 0);
        const newVal = value.slice(0, start) + suggestion + value.slice(end);
        onChange(name, newVal);
        setFiltered([]);
        textarea.focus();
    }

    function handleKeyDown(e: React.KeyboardEvent) {
        if (filtered.length === 0) return;
        if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Tab" || e.key === "Enter" || e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
        }
        if (e.key === "ArrowDown") {
            setSelected(s => Math.min(s + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            setSelected(s => Math.max(s - 1, 0));
        } else if (e.key === "Tab" || e.key === "Enter") {
            applySuggestion(filtered[selected]);
        } else if (e.key === "Escape") {
            setFiltered([]);
        }
    }

    const pos = filtered.length > 0 ? getDropdownPos() : null;

    const dropdown = pos && createPortal(
        <ul ref={listRef} className="tag-suggestions-portal" style={{
            top: pos.top - 4,
            left: pos.left,
            width: pos.width,
            transform: "translateY(-100%)"
        }}>
            {filtered.map((s, i) => (
                <li key={s} className={i === selected ? "active" : ""}
                    onMouseDown={() => applySuggestion(s)}>{s}</li>
            ))}
        </ul>,
        document.body
    );

    return (
        <div className="tag-textarea-wrapper">
            <textarea
                ref={ref}
                name={name}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={() => setTimeout(() => setFiltered([]), 150)}
            />
            {dropdown}
        </div>
    );
}